/* eslint-disable no-unused-vars */
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useQuery, useQueryClient } from "react-query";

import { updateConfigurationApi } from "../../../Common/utils/api/api";
import t from "../../../Common/utils/translation/translator";
import useCheckMobileScreen from "../../../Common/utils/useCheckMobileScreen";
import {
  selectUpdateConfiguration,
  setUpdatingConfiguration,
  updateConfiguration
} from "../../../Common/store/slices/common";
import {
  selectMc12Configuration,
  selectMc12CurrentStep,
  selectMc12Steps,
  selectReadOnlyMode,
  setMc12CurrentStep,
  selectTrack
} from "../../store/slices/mc12";
import { showSnackbar } from "../../../Retrofit/store/slices/retrofit";

import Stepper from "shared-components/lib/iac-stepper";
import {
  buildVirtualPageViewDataLayerEvent,
  logDataLayerEvent
} from "../../../Common/utils/dataLayerAnalytics/dataLayerAnalytics";

/**
 * Sub header of the app.
 */
const Mc12Stepper = () => {
  const dispatch = useDispatch();
  const steps = useSelector(selectMc12Steps);
  const currentStep = useSelector(selectMc12CurrentStep);
  const configuration = useSelector(selectMc12Configuration);
  const isMobile = useCheckMobileScreen();
  const track = useSelector(selectTrack);

  useEffect(() => {
    logDataLayerEvent(buildVirtualPageViewDataLayerEvent(configuration?.configId, currentStep?.label));
  }, [currentStep]);

  const runUpdateConfiguration = useSelector(selectUpdateConfiguration);

  const modeReadOnly = useSelector(selectReadOnlyMode);
  const queryClient = useQueryClient();

  const [savedConfigurationPayload, setSavedConfigurationPayload] = useState(null);

  const {
    refetch: execUpdateConfiguration,
    isLoading,
    isFetching
  } = useQuery(
    "UPDATE_MC12_CONFIGURATION",
    async () => {
      try {
        if (JSON.stringify(savedConfigurationPayload) !== JSON.stringify(configuration.configData)) {
          setSavedConfigurationPayload(configuration.configData);
          return await updateConfigurationApi(configuration.configId, configuration.configData);
        }
        return null;
      } catch (error) {
        console.error("ERR", error);
      }
      return undefined;
    },
    {
      enabled: false
    }
  );

  useEffect(() => {
    dispatch(setUpdatingConfiguration(isLoading || isFetching));
  }, [isLoading, isFetching]);

  useEffect(() => {
    if (configuration?.configId && currentStep && !modeReadOnly) {
      // console.log("Configuration changed => update CONF");
      queryClient.cancelQueries("UPDATE_CONFIGURATION");
      execUpdateConfiguration();
    }
  }, [runUpdateConfiguration, configuration]);

  const handleSelectionSettings = (step) => {
    dispatch(setMc12CurrentStep(step));
    dispatch(updateConfiguration());
  };

  const handleGoToElectricalInstallation = () => {
    dispatch(setMc12CurrentStep(steps.ELECTRICAL_INSTALLATION));
    dispatch(updateConfiguration());
  };

  const handleGoToCommunication = () => {
    dispatch(setMc12CurrentStep(steps.COMMUNICATION));
    dispatch(updateConfiguration());
  };

  const handleGoToAccessories = () => {
    dispatch(setMc12CurrentStep(steps.ACCESSORIES));
    dispatch(updateConfiguration());
  };

  const handleGoToFinalize = () => {
    dispatch(setMc12CurrentStep(steps.FINALIZE));
    dispatch(updateConfiguration());
  };

  function buildYourTrackContinueCondition() {
    const condition =
      !track.shouldRecalculateGuideRails &&
      track.length > 0 &&
      track.calculatedCarriers.reduce((acc, carrier) => {
        if (carrier.quantityPerSet === "10_PIECES") {
          return acc + carrier.quantity * 10;
        } else {
          return acc + carrier.quantity;
        }
      }, 0) > 0;
    // console.log("buildYourTrackContinueCondition", condition);
    return condition;
  }

  function electricalInstallationContinueCondition() {
    const condition = track.calculatedGuideRails.length > 0 && !track.shouldRecalculateElectricalInstallation;
    // console.log("electricalInstallationContinueCondition", condition);
    return condition;
  }

  function communicationContinueCondition() {
    const condition =
      track.calculatedElectricalInstallation.powerGroups.length > 0 && !track.shouldRecalculateCommunication;
    // console.log("communicationContinueCondition", condition);
    return condition;
  }

  function accessoriesContinueCondition() {
    const condition = currentStep.id > steps.ACCESSORIES.id;
    // console.log("accessoriesContinueCondition", condition);
    return condition;
  }

  function finalizeContinueCondition() {
    const condition = currentStep.id === steps.FINALIZE.id;
    // console.log("finalizeContinueCondition", condition);
    return condition;
  }

  function showMc12Snackbars() {
    if (track.shouldRecalculateGuideRails) {
      dispatch(showSnackbar({ id: "MC12_RECALCULATE_GUIDERAILS" }));
    } else if (!buildYourTrackContinueCondition()) {
      dispatch(showSnackbar({ id: "MISSING_OR_INCORRECT_FIELD" }));
    }
  }

  const mc12Stepper = currentStep !== steps.START && (
    <>
      <Stepper
        steps={[
          {
            action: () => handleSelectionSettings(steps.BUILD_TRACK),
            active: true,
            done: buildYourTrackContinueCondition() && currentStep.id > steps.BUILD_TRACK.id,
            hidden: false,
            id: "BUILD_TRACK",
            isActiveStep: currentStep === steps.BUILD_TRACK,
            isActive: currentStep === steps.BUILD_TRACK,
            label: t(steps.BUILD_TRACK.label),
            name: "BUILD_TRACK",
            order: 1,
            skipped: false,
            subLabel:
              currentStep === steps.BUILD_TRACK ? t("msizer.ui.stepper.in_progress") : t("msizer.ui.stepper.done")
          },
          {
            action: () => handleSelectionSettings(steps.ELECTRICAL_INSTALLATION),
            active: buildYourTrackContinueCondition(),
            done: buildYourTrackContinueCondition(),
            hidden: false,
            id: "ELECTRICAL_INSTALLATION",
            isActiveStep: currentStep === steps.ELECTRICAL_INSTALLATION,
            isActive: currentStep === steps.ELECTRICAL_INSTALLATION,
            label: t(steps.ELECTRICAL_INSTALLATION.label),
            name: "ELECTRICAL_INSTALLATION",
            order: 2,
            skipped: false,
            subLabel:
              currentStep === steps.ELECTRICAL_INSTALLATION
                ? t("msizer.ui.stepper.in_progress")
                : electricalInstallationContinueCondition()
                ? t("msizer.ui.stepper.done")
                : t("msizer.ui.stepper.not_done")
          },
          {
            action: () => handleSelectionSettings(steps.COMMUNICATION),
            active: buildYourTrackContinueCondition() && electricalInstallationContinueCondition(),
            done: communicationContinueCondition(),
            hidden: false,
            id: "COMMUNICATION",
            isActiveStep: currentStep === steps.COMMUNICATION,
            isActive: currentStep === steps.COMMUNICATION,
            label: t(steps.COMMUNICATION.label),
            name: "COMMUNICATION",
            order: 3,
            skipped: false,
            subLabel:
              currentStep === steps.COMMUNICATION
                ? t("msizer.ui.stepper.in_progress")
                : communicationContinueCondition()
                ? t("msizer.ui.stepper.done")
                : t("msizer.ui.stepper.not_done")
          },
          {
            action: () => handleSelectionSettings(steps.ACCESSORIES),
            active:
              buildYourTrackContinueCondition() &&
              electricalInstallationContinueCondition() &&
              communicationContinueCondition(),
            done: accessoriesContinueCondition(),
            hidden: false,
            id: "ACCESSORIES",
            isActiveStep: currentStep === steps.ACCESSORIES,
            isActive: currentStep === steps.ACCESSORIES,
            label: t(steps.ACCESSORIES.label),
            name: "ACCESSORIES",
            order: 4,
            skipped: false,
            subLabel:
              currentStep === steps.ACCESSORIES
                ? t("msizer.ui.stepper.in_progress")
                : accessoriesContinueCondition()
                ? t("msizer.ui.stepper.done")
                : t("msizer.ui.stepper.not_done")
          },
          {
            action: () => handleSelectionSettings(steps.FINALIZE),
            active:
              buildYourTrackContinueCondition() &&
              electricalInstallationContinueCondition() &&
              communicationContinueCondition(),
            done: currentStep === steps.FINALIZE,
            hidden: false,
            id: "FINALIZE",
            isActiveStep: currentStep === steps.FINALIZE,
            isActive: currentStep === steps.FINALIZE,
            label: t(steps.FINALIZE.label),
            name: "FINALIZE",
            order: 5,
            skipped: false,
            subLabel: currentStep === steps.FINALIZE ? t("msizer.ui.stepper.done") : t("msizer.ui.stepper.not_done")
          }
        ]}
        mobileBreakpoint={800}
        disabled={false}
      ></Stepper>
    </>
  );

  return mc12Stepper;
};

Mc12Stepper.propTypes = {};

Mc12Stepper.defaultProps = {};

export default Mc12Stepper;
